const getSourceEvent = ({ bookingId, editedEntry, entryId, eventsData }) => {
  return (eventsData || []).find((event) => event.id === entryId)
    || (eventsData || []).find((event) => event.bookingId === bookingId)
    || editedEntry;
};

const findEquipmentMeta = ({ bookingInstruments, entryItems, resourceId }) => {
  const entryEquipment = entryItems.find((item) => item.resource === resourceId)?.equipment;
  if (entryEquipment) {
    return entryEquipment;
  }

  return (bookingInstruments || []).find(
    (equipment) => (equipment.inventoryId || equipment.id) === resourceId,
  );
};

const getEquipmentTransitionFlags = ({ draft, newMeta, original, originalMeta }) => {
  const resourceChanged = original.resource !== draft.resource;
  const originalRequiresHazardousHandling = Boolean(Number(originalMeta?.isHazardous || 0));
  const newRequiresHazardousHandling = Boolean(Number(newMeta?.isHazardous || 0));
  const originalRequiresAssistance = Boolean(originalMeta?.assistedBooking);
  const newRequiresAssistance = Boolean(newMeta?.assistedBooking);

  return {
    assistanceFalseToTrue: resourceChanged && !originalRequiresAssistance && newRequiresAssistance,
    hazardousFalseToTrue:
      resourceChanged && !originalRequiresHazardousHandling && newRequiresHazardousHandling,
  };
};

const buildMutationEntries = ({ draft, entryId, entryItems, updateRequest }) => {
  return entryItems.map((item) => {
    const isModified = item.id === entryId;

    return {
      allDay: false,
      dateFrom: isModified ? draft.start : item.start,
      dateTo: isModified ? draft.end : item.end,
      inventoryId: isModified ? draft.resource : item.resource,
      bookingEntryId: item.id,
      commandType: updateRequest,
    };
  });
};

const getApprovalInfo = ({ sourceEntry, sourceEvent }) => {
  return sourceEntry?.bookingApprovalInfo || sourceEvent?.bookingApprovalInfo || {};
};

const getHazardousDeclaration = (approval) => {
  if (!approval?.hazardousDeclaration) {
    return { hasHazardousMaterials: false, hazardousMaterialsList: "" };
  }

  return {
    hasHazardousMaterials: approval.hazardousDeclaration.hasHazardousMaterials ?? false,
    hazardousMaterialsList: approval.hazardousDeclaration.hazardousMaterialsList || "",
  };
};

const getAssistanceDeclaration = ({ approval, assistanceFalseToTrue }) => {
  return {
    requiresAssistance: assistanceFalseToTrue
      ? false
      : approval?.assistanceDeclaration?.requiresAssistance ?? false,
    assistanceReason: approval?.assistanceDeclaration?.assistanceReason || "",
  };
};

export const getTimelineUpdateContext = ({
  bookingId,
  bookingInstruments,
  draft,
  editedEntry,
  entryId,
  entryItems,
  eventsData,
  original,
}) => {
  const sourceEntry = entryItems.find((item) => item.id === entryId);
  const sourceEvent = getSourceEvent({ bookingId, editedEntry, entryId, eventsData });
  const originalMeta = findEquipmentMeta({ bookingInstruments, entryItems, resourceId: original.resource }) || {};
  const newMeta = findEquipmentMeta({ bookingInstruments, entryItems, resourceId: draft.resource }) || {};
  const transitionFlags = getEquipmentTransitionFlags({ draft, newMeta, original, originalMeta });

  return {
    sourceEntry,
    sourceEvent,
    newMeta,
    ...transitionFlags,
  };
};

export const buildHazardousTimelineEditState = ({
  bookingId,
  draft,
  emailToBookingForObj,
  entryId,
  entryItems,
  getBookingRange,
  singleEventSkeleton,
  sourceEntry,
  sourceEvent,
  updatedEquipmentMeta,
}) => {
  const skeletonKeys = Object.keys(singleEventSkeleton);
  const bookingEntries = entryItems.map((item) => {
    const isModified = item.id === entryId;
    const rebuilt = {};

    skeletonKeys.forEach((key) => {
      rebuilt[key] = item[key] ?? null;
    });

    return {
      ...rebuilt,
      inventoryId: isModified ? draft.resource : item.resource,
      equipment: isModified ? updatedEquipmentMeta : item.equipment,
      start: isModified ? draft.start : item.start,
      end: isModified ? draft.end : item.end,
      title: item.type,
      description: item?.description,
      elnId: item?.elnId,
      project: item?.project,
      redPandaProjects: item?.redPandaProjects,
      createdBy: item?.createdBy,
      reservedForEmails: item?.reservedForEmails,
    };
  });

  const { dateFrom: bookingDateFrom, dateTo: bookingDateTo } = getBookingRange(
    bookingEntries,
    "start",
    "end",
    draft.start,
    draft.end,
  );
  const bookedUsers = (sourceEntry?.reservedForEmails || sourceEvent?.reservedForEmails || [])
    .map(emailToBookingForObj);

  return {
    editBookingData: {
      ...sourceEvent,
      id: bookingId,
      bookingId,
      bookingEntries,
      bookedEquipments: Array.from(new Set(bookingEntries.map((entry) => entry.inventoryId))),
      bookedUsers,
      bookingFor: bookedUsers,
      reservedForEmails: sourceEntry?.reservedForEmails || sourceEvent?.reservedForEmails || [],
      hazardousDeclaration: sourceEntry?.bookingApprovalInfo?.hazardousDeclaration,
      assistanceDeclaration: sourceEntry?.bookingApprovalInfo?.assistanceDeclaration,
      bookingApprovalInfo: sourceEntry?.bookingApprovalInfo,
      elnId: sourceEntry?.elnId || sourceEvent?.elnId || null,
      startAtStep: 3,
      dateFrom: bookingDateFrom,
      dateTo: bookingDateTo,
    },
    pendingReservations: bookingEntries.map((entry) => ({
      ...entry,
      resource: entry.inventoryId,
      isEditEventFlag: true,
    })),
  };
};

export const buildTimelineMutationVariables = ({
  assistanceFalseToTrue,
  bookingId,
  draft,
  entryId,
  entryItems,
  getBookingRange,
  hasBookingAdminRole,
  sourceEntry,
  sourceEvent,
  transformElnIdForMutation,
  updateRequest,
  user,
}) => {
  const entries = buildMutationEntries({ draft, entryId, entryItems, updateRequest });

  const { dateFrom: bookingDateFrom, dateTo: bookingDateTo } = getBookingRange(
    entries,
    "dateFrom",
    "dateTo",
    draft.start,
    draft.end,
  );
  const approval = getApprovalInfo({ sourceEntry, sourceEvent });
  const hazardousDeclaration = getHazardousDeclaration(approval);
  const assistanceDeclaration = getAssistanceDeclaration({ approval, assistanceFalseToTrue });

  return {
    commandType: updateRequest,
    createdBy: user?.user,
    createdByEmail: user?.email,
    createdByName: user?.name || user?.email,
    entries,
    type: sourceEntry?.type || sourceEvent?.type || sourceEvent?.title || "AVAILABLE",
    description: sourceEntry?.description || sourceEvent?.description || "",
    project: sourceEntry?.project || sourceEvent?.project || "",
    redPandaProjects: sourceEntry?.redPandaProjects || sourceEvent?.redPandaProjects || [],
    remark: "",
    bookingId,
    dateFrom: bookingDateFrom,
    dateTo: bookingDateTo,
    site: sourceEntry?.site || user?.site,
    reservedForEmails: sourceEntry?.reservedForEmails || sourceEvent?.reservedForEmails || [user?.email],
    reservedForUsers: sourceEvent?.reservedForUsers || [user?.user],
    hazardousDeclaration,
    assistanceDeclaration,
    elnId: transformElnIdForMutation(sourceEntry?.elnId || sourceEvent?.elnId),
    force: hasBookingAdminRole,
  };
};